/* eslint-disable import/no-anonymous-default-export */
import * as React from "react";
import { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { useModalContext } from "../../../components/modal";
import { useSnackbarContext } from "../../../components/snackbar";
import { mapValues } from "lodash";
import { format } from "date-fns";
import { Input } from "components/input";
import { Button, NoStyleButton, OutlineButton } from "components/button";
import AssigneeSelect from "components/react-select/assignees";
import { theme } from "styles/theme";
import Switch from "rc-switch";
import MaskedDurationInput from "components/input/MaskedDurationInput";
import { parseDuration } from "utils/duration";
import { DateObject } from "react-multi-date-picker";
import Field from "components/field";
import GroupedButton from "components/button/grouped";
import AppointmentType from "components/react-select/appointment-type";
import LineItemsContainer from "containers/lineItems";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { companyClient } from "api/api";
import { VscWarning } from "react-icons/vsc";
import DatePicker from "react-multi-date-picker";
import { useTranslation } from "react-i18next";
import { convertFromUTC, convertToUTC } from "utils/utcConversion";
import { formatInTimeZone } from "date-fns-tz";
import { useAuthContext } from "contexts/auth";
import {
  isIsoDateWithinSupportedRange,
  parseIsoDateWithinRange,
} from "utils/dateValidation";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { CgUndo } from "react-icons/cg";
import TooltippedElement from "components/TooltippedElement";
import { AppointmentModalChecklistsSection } from "./AppointmentModalChecklistsSection";
import { useFormState } from "./useFormState";
import { useChecklistRows } from "./useChecklistRows";

// ============================================================================
// Types
// ============================================================================

type AssigneeOption = { value: number | string; label: string };

/** Form values managed by the in-modal `useFormState`. */
type FormValues = {
  date: string;
  start_time_utc: string;
  end_time_utc: string;
  duration: string;
  assignee: AssigneeOption[];
  note: string;
  estimate_amount: number | "";
  completion_note: string;
  type: "job" | "estimate";
  fk_appointment_type_id: string;
  fk_appointment_type_default_id: string | null;
  estimate_products: unknown[] | null;
  estimate_pricetype: "sale" | "install";
  is_reminding: boolean;
  /** `useFormState`'s generic constraint requires `Record<string, unknown>` shape compatibility. */
  [key: string]: unknown;
};

/**
 * Subset of the appointment object actually read by this modal. Kept narrow
 * on purpose; tightening this further is a follow-up once the BE response
 * shape is properly typed across the app.
 */
type AppointmentInput = {
  id?: number | string;
  fk_appointment_type_id?: number | string | null;
  fk_appointment_type_default_id?: number | string | null;
  is_booked?: boolean;
  status?: { label?: string };
  date?: string;
  start_time_utc?: string;
  end_time_utc?: string;
  duration?: number | string;
  assignee?: AssigneeOption[];
  note?: string;
  estimate_amount?: number | null;
  completion_note?: string;
  type?: string;
  is_reminding?: boolean;
};

type AppointmentTypeDefault = {
  id: number | string;
  active: boolean;
  label?: string;
  description?: string;
  duration?: number | string;
  assigned_to?: Array<number | string> | string | null;
  completion_note?: string;
  date?: string;
  start_time_utc?: string;
  end_time_utc?: string;
  category?: string;
  send_reminder?: boolean | null;
  email_confirmation?: boolean | null;
  item_target_id?: number | string | null;
  fk_appointment_type_id?: number | string | null;
};

type AppointmentTypeRow = AppointmentTypeDefault & {
  defaults?: AppointmentTypeDefault[];
};

type ResponseOption = { response: string };
type ResponseRow = {
  id: number | string;
  question_label: string;
  options: ResponseOption[];
};

type Props = {
  parentId?: number | string | null;
  parentType?: string | null;
  id: number | string;
  type: string;
  closed: () => void;
  appointmentId?: number | string;
  appointment?: AppointmentInput;
  emailDefault?: boolean;
  has_est?: boolean;
  isBookit?: boolean;
  responses?: ResponseRow[];
  // Bucket-1 cleanup: `acceptedBid` and `defaultdate` props were never read.
  // Removed from the public surface (audit 006 §1).
};

type AppointmentLineItemsOffer = {
  itemTargetId: number | string;
  lineItemsType: "appointment-type" | "appointment-type-default";
  lineItemsId: string;
  parentId?: string;
};

type ValuesSnapshotForRevert = FormValues & { _sendEmail?: boolean };

// ============================================================================
// Module-level helpers
// ============================================================================

const isNonEmpty = (val: unknown): boolean => {
  if (val === null || val === undefined || val === "" || val === false) return false;
  if (Array.isArray(val) && val.length === 0) return false;
  return true;
};

/** Same URL shape as LineItemsContainer products fetch (must match its react-query key). */
const buildLineItemsProductsPath = (
  lineItemsType: AppointmentLineItemsOffer["lineItemsType"],
  lineItemsId: string,
  parentId?: string
): string => {
  const params = new URLSearchParams();
  if (parentId != null) params.set("parent_id", parentId);
  const qs = params.toString();
  return `/products/${lineItemsType}/${lineItemsId}${qs ? `?${qs}` : ""}`;
};

/** Coerce a possibly-string-or-number duration value into the `number | null`
 *  shape `parseDuration` expects. Empty string and `null`/`undefined` → null. */
const toDurationNumber = (value: number | string | null | undefined): number | null => {
  if (value == null || value === "") return null;
  if (typeof value === "number") return value;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Form stores the user's typed digits right-justified (calculator style):
 *  newest digit at right, older digits shift left. "1" = 1m, "100" = 1h 0m,
 *  "1234" = 12h 34m. Pad-LEFT to 4 to convert to HHMM for the BE. */
const toApiDurationString = (duration: unknown): string | null => {
  if (duration == null || duration === "") return null;
  const digits = String(duration).replace(/[^0-9]/g, "");
  if (!digits) return null;
  return digits.padStart(4, "0").slice(-4);
};

/** BE minutes → form's typed-digits representation. 60 → "100", 1 → "1",
 *  754 → "1234". Strips leading zeros so what comes back from the BE matches
 *  the digits the user actually typed before submit. */
const minutesToTypedDigits = (minutes: number | null | undefined): string => {
  if (minutes == null) return "";
  const fullStr = parseDuration(minutes).fullStr;
  if (!fullStr) return "";
  return fullStr.replace(/^0+/, "");
};

const getAppointmentEmailConfig = async () => {
  const res = await companyClient.get(`/appointment/emailConfirmation`);
  return res.data;
};

const getEmployeeNamesByIds = async (
  employeeIds: Array<number | string>
): Promise<Array<{ id: number | string; first_name: string; last_name: string }>> => {
  const res = await companyClient.get(`/employee/names`, {
    params: { employeeIds },
  });
  return res.data;
};

/**
 * Single-source validation that replaces the previous Yup schema.
 * Only the `date` field needs validation; the other Yup fields were nullable
 * no-ops, and `tech_assigned` referenced a field that doesn't exist on the form.
 * Returns an empty object when there are no errors.
 */
const validate = (values: FormValues, invalidDateMessage: string) => {
  const errors: Partial<Record<keyof FormValues, string>> = {};
  if (values.date && !isIsoDateWithinSupportedRange(values.date)) {
    errors.date = invalidDateMessage;
  }
  return errors;
};

// ============================================================================
// Component
// ============================================================================

export default function AppointmentModal({
  parentId = null,
  parentType = null,
  id,
  type,
  closed,
  appointmentId,
  appointment,
  emailDefault,
  has_est = false,
  isBookit = false,
  responses = [],
}: Props) {
  const { t, i18n } = useTranslation();
  const tBase = "containers.appointmentCard.appointment-folder.appointmentModal";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const invalidDateMessage = tr("Please enter a valid date (YYYY-MM-DD).");

  const { setModal } = useModalContext();
  const { showSnackbar } = useSnackbarContext();
  const { timezone } = useAuthContext();
  const effectiveTz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [isEstimating, setIsEstimating] = useState(false);
  const [sendEmail, setSendEmail] = useState<boolean | undefined>(emailDefault);
  const [isFlyoutVisible, setIsFlyoutVisible] = useState(false);
  /** Late-bound bridge: `useChecklistRows` is created later in render order
   *  (it depends on `form.values`, which depends on `initialValues`), but the
   *  `saveAppt` mutation's `onSuccess` needs to call `flushPending`. We mirror
   *  the hook's flush function into this ref each render so onSuccess can
   *  read it lazily at invocation time. */
  const flushChecklistsRef = React.useRef<((apptId: number) => Promise<void>) | null>(null);
  const [appointmentEmailConfig, setAppointmentEmailConfig] = useState<unknown>();
  const [selectedTypeId, setSelectedTypeId] = useState<string>(
    appointment?.fk_appointment_type_id?.toString() || ""
  );
  const [selectedDefaultId, setSelectedDefaultId] = useState<string>(
    appointment?.fk_appointment_type_default_id?.toString() || "new"
  );

  const [valuesBeforeDefaultFill, setValuesBeforeDefaultFill] = useState<
    ValuesSnapshotForRevert | null
  >(null);
  const [defaultModifiedFields, setDefaultModifiedFields] = useState<Set<string>>(
    new Set()
  );
  const [apptTypeLineItemSource, setApptTypeLineItemSource] = useState<
    AppointmentLineItemsOffer | null
  >(null);
  const [apptTypeItemTargetId, setApptTypeItemTargetId] = useState<
    number | string | null
  >(null);
  const [pendingApptTypeLineItems, setPendingApptTypeLineItems] = useState<
    AppointmentLineItemsOffer | null
  >(null);
  const [apptTypeLineItemsAdded, setApptTypeLineItemsAdded] = useState(false);
  const [isLineItemsPrefetching, setIsLineItemsPrefetching] = useState(false);

  const queryClient = useQueryClient();

  const isProgrammaticDefaultChange = React.useRef(false);
  const lastAppliedChildDefaultRef = React.useRef<string | null>(null);

  useEffect(() => {
    const appointmentTypeId = appointment?.fk_appointment_type_id?.toString() || "";
    let appointmentDefaultId: string = "new";
    if (appointment?.fk_appointment_type_default_id) {
      appointmentDefaultId = appointment.fk_appointment_type_default_id.toString();
    } else if (appointmentTypeId) {
      appointmentDefaultId = "parent-applied";
    }
    setSelectedTypeId(appointmentTypeId);
    setSelectedDefaultId(appointmentDefaultId);
    lastAppliedChildDefaultRef.current =
      appointmentDefaultId === "new" || appointmentDefaultId === "parent-applied"
        ? null
        : appointmentDefaultId;
    // Note: this effect runs on initial mount AND whenever the appointment
    // prop's id/type/default ids change. It is NOT a user-initiated event.
    // The checklist row state (in `useChecklistRows`) rebuilds itself from
    // the resulting (typeNum, defaultNum, allowAutoOffer) — no opt-in needed.
  }, [
    appointment?.id,
    appointment?.fk_appointment_type_id,
    appointment?.fk_appointment_type_default_id,
  ]);

  const buildDefaultChanges = async (source: AppointmentTypeRow | AppointmentTypeDefault) => {
    const changes: Partial<FormValues> = {};
    const changedFields = new Set<string>();

    if (source.duration != null) {
      changes.duration = minutesToTypedDigits(toDurationNumber(source.duration));
      changedFields.add("duration");
    }
    if (source.description != null) {
      changes.note = source.description;
      changedFields.add("note");
    }
    if (source.assigned_to != null && source.assigned_to !== "") {
      const employeeNames = await getEmployeeNamesByIds(
        Array.isArray(source.assigned_to) ? source.assigned_to : [source.assigned_to]
      );
      if (employeeNames.length) {
        changes.assignee = employeeNames.map((e) => ({
          value: e.id,
          label: `${e.first_name} ${e.last_name}`,
        }));
        changedFields.add("assignee");
      }
    }
    if (source.completion_note != null) {
      changes.completion_note = source.completion_note;
      changedFields.add("completion_note");
    }
    if (source.date) {
      changes.date = String(source.date).split("T")[0];
      changedFields.add("date");
    }
    if (source.start_time_utc) {
      try {
        changes.start_time_utc = formatInTimeZone(
          new Date(source.start_time_utc),
          effectiveTz,
          "HH:mm"
        );
      } catch {
        changes.start_time_utc = convertFromUTC(source.start_time_utc, timezone, false);
      }
      changedFields.add("start_time_utc");
    }
    if (source.end_time_utc) {
      try {
        changes.end_time_utc = formatInTimeZone(
          new Date(source.end_time_utc),
          effectiveTz,
          "HH:mm"
        );
      } catch {
        changes.end_time_utc = convertFromUTC(source.end_time_utc, timezone, false);
      }
      changedFields.add("end_time_utc");
    }
    if (source.category) {
      changes.type = source.category === "estimate" ? "estimate" : "job";
      changedFields.add("type");
    }
    if (source.send_reminder !== undefined && source.send_reminder !== null) {
      changes.is_reminding = !!source.send_reminder;
      changedFields.add("is_reminding");
    } else if (source.send_reminder === null) {
      changes.is_reminding = false;
      changedFields.add("is_reminding");
    }

    return { changes, changedFields };
  };

  const fieldValuesEqualForUndo = (
    fieldName: string,
    previousVal: unknown,
    incomingVal: unknown
  ): boolean => {
    if (previousVal === incomingVal) return true;
    if (previousVal == null && incomingVal == null) return true;
    if (fieldName === "assignee") {
      const idsFrom = (arr: unknown) =>
        (Array.isArray(arr) ? arr : [])
          .map((x) => (x as { value?: unknown })?.value)
          .filter((x) => x != null)
          .slice()
          .sort((x, y) => Number(x) - Number(y));
      const a = idsFrom(previousVal);
      const b = idsFrom(incomingVal);
      if (a.length !== b.length) return false;
      return a.every((idVal, i) => String(idVal) === String(b[i]));
    }
    if (typeof previousVal === "boolean" || typeof incomingVal === "boolean") {
      return Boolean(previousVal) === Boolean(incomingVal);
    }
    const prevStr = previousVal == null || previousVal === "" ? "" : String(previousVal).trim();
    const nextStr = incomingVal == null || incomingVal === "" ? "" : String(incomingVal).trim();
    return prevStr === nextStr;
  };

  const computeModifiedFields = (
    changedFields: Set<string>,
    previousValues: Partial<FormValues>,
    changes: Partial<FormValues>
  ): Set<string> => {
    const modified = new Set<string>();
    for (const field of changedFields) {
      if (!Object.prototype.hasOwnProperty.call(changes, field)) continue;
      const prev = (previousValues as Record<string, unknown>)[field];
      if (!isNonEmpty(prev)) continue;
      const next = (changes as Record<string, unknown>)[field];
      if (fieldValuesEqualForUndo(field, prev, next)) continue;
      modified.add(field);
    }
    return modified;
  };

  const appointmentTypesQuery = useQuery<AppointmentTypeRow[]>(
    "appointment-types-with-defaults-modal",
    async () => (await companyClient.get(`/appointment-types/with-defaults`)).data,
    { staleTime: 60000 }
  );

  const selectedType = useMemo(() => {
    if (!selectedTypeId || !appointmentTypesQuery.data) return null;
    return appointmentTypesQuery.data.find(
      (tp) => String(tp.id) === String(selectedTypeId)
    );
  }, [selectedTypeId, appointmentTypesQuery.data]);

  const pendingLineItemsCountQuery = useQuery<number>(
    [
      "appointment-modal-pending-line-items-count",
      pendingApptTypeLineItems?.lineItemsType,
      pendingApptTypeLineItems?.lineItemsId,
      pendingApptTypeLineItems?.parentId,
    ],
    async () => {
      if (!pendingApptTypeLineItems) return 0;
      const path = buildLineItemsProductsPath(
        pendingApptTypeLineItems.lineItemsType,
        pendingApptTypeLineItems.lineItemsId,
        pendingApptTypeLineItems.parentId
      );
      const res = await companyClient.get(path);
      const products = res.data?.products;
      return Array.isArray(products) ? products.length : 0;
    },
    {
      enabled: Boolean(pendingApptTypeLineItems),
      staleTime: 30000,
    }
  );

  const pendingLineItemsOfferCount = pendingLineItemsCountQuery.data ?? 0;

  const checklistDefaultsCountQuery = useQuery<number>(
    [
      "appointment-modal-checklist-defaults-count",
      selectedTypeId,
      selectedDefaultId,
    ],
    async () => {
      const params = new URLSearchParams();
      params.set("fk_appointment_type_id", String(selectedTypeId));
      const d = String(selectedDefaultId);
      if (d && d !== "new" && d !== "parent-applied") {
        params.set("fk_appointment_type_default_id", d);
      }
      const res = await companyClient.get(
        `/appointment-checklists/defaults-count?${params.toString()}`
      );
      return typeof res.data?.count === "number" ? res.data.count : 0;
    },
    {
      enabled: Boolean(selectedTypeId),
      staleTime: 30000,
    }
  );

  const defaultOptions = useMemo(() => {
    const localTr = (key: string) => t(`${tBase}.${key}`);
    const options: Array<{ value: string; label: string }> = [
      { value: "new", label: localTr("(No auto-fill)") },
    ];

    if (selectedType) {
      const activeDefaults = (selectedType.defaults || []).filter((d) => d.active);
      if (activeDefaults.length > 0) {
        options.push({ value: "parent-applied", label: "(Default)" });
        activeDefaults.forEach((d) => {
          options.push({
            value: d.id.toString(),
            label: d.label || localTr("Unnamed Default"),
          });
        });
      }
    }
    return options;
  }, [selectedType, t, tBase]);

  const selectedDefaultOption = useMemo(
    () => defaultOptions.find((opt) => String(opt.value) === String(selectedDefaultId)),
    [defaultOptions, selectedDefaultId]
  );

  const getAppointmentEmailConfigMutation = useMutation(getAppointmentEmailConfig, {
    onSuccess: (data) => {
      setAppointmentEmailConfig(data);
    },
  });

  // -----------------------------------------------------------------
  // Save flow — single mutation parameterized by mode (audit 006 §14.Q4)
  // -----------------------------------------------------------------

  type SavePayload = Record<string, unknown>;
  type SaveInput = {
    mode: "create" | "update";
    payload: SavePayload;
    /** When true, the per-sibling lifecycle is skipped: no checklist flush,
     *  no broad invalidation, no snackbar, no modal close. Used for multi-day
     *  fan-out so we don't fire N+1 snackbars. */
    isMultiDaySibling?: boolean;
  };

  const saveAppt = useMutation(
    async (input: SaveInput) => {
      const url =
        input.mode === "create"
          ? `/appointment/${type}/${id}`
          : `/appointment/${appointmentId}/update`;
      const res = await companyClient.post(url, input.payload);
      return res.data as { id?: number | string };
    },
    {
      onSuccess: async (res, input) => {
        if (input.isMultiDaySibling) {
          // Siblings are part of the multi-day fan-out; the parent's own
          // onSuccess handles all the lifecycle work once.
          return;
        }
        const apptId =
          input.mode === "create"
            ? Number(res?.id)
            : Number(appointmentId);
        try {
          if (Number.isFinite(apptId) && flushChecklistsRef.current) {
            await flushChecklistsRef.current(apptId);
          }
        } catch (err) {
          console.error(err);
          showSnackbar(tr("Checklists flush error"));
        }
        if (Number.isFinite(apptId)) {
          queryClient.invalidateQueries(["products", String(apptId), "appointment"]);
          queryClient.invalidateQueries(["appointment-checklists", apptId]);
        }
        // Refresh parent views — without these, the user sees stale data
        // (e.g. /projects/:id with old appointment counts) until a hard
        // reload, even though the BE persisted the change correctly.
        queryClient.invalidateQueries("appointments");
        queryClient.invalidateQueries("project");
        queryClient.invalidateQueries("request");
        showSnackbar(
          input.mode === "create" ? tr("Appointment Added!") : tr("Appointment Updated!")
        );
        closed();
        setModal();
      },
    }
  );

  const handleShowRequestData = () => {
    setIsFlyoutVisible(!isFlyoutVisible);
  };

  const syncLineItemsOfferFromSource = (source: AppointmentTypeRow | AppointmentTypeDefault | Record<string, never>) => {
    const targetId =
      "item_target_id" in source &&
      source.item_target_id != null &&
      source.item_target_id !== ""
        ? source.item_target_id
        : null;
    if (!targetId) {
      setPendingApptTypeLineItems(null);
      setApptTypeLineItemsAdded(false);
      setApptTypeLineItemSource(null);
      setApptTypeItemTargetId(null);
      return;
    }
    const isChild =
      "fk_appointment_type_id" in source &&
      source.fk_appointment_type_id != null &&
      source.fk_appointment_type_id !== "";
    setPendingApptTypeLineItems({
      itemTargetId: targetId,
      lineItemsType: isChild ? "appointment-type-default" : "appointment-type",
      lineItemsId: String((source as AppointmentTypeRow).id),
      parentId: isChild
        ? String((source as AppointmentTypeDefault).fk_appointment_type_id)
        : undefined,
    });
    setApptTypeLineItemsAdded(false);
    setApptTypeLineItemSource(null);
    setApptTypeItemTargetId(null);
  };

  const handleAddAppointmentTypeLineItems = async () => {
    if (!pendingApptTypeLineItems || isLineItemsPrefetching) return;
    const { lineItemsType, lineItemsId, parentId, itemTargetId } = pendingApptTypeLineItems;

    setIsLineItemsPrefetching(true);
    try {
      const productsPath = buildLineItemsProductsPath(lineItemsType, lineItemsId, parentId);
      if (appointment && appointmentId) {
        const sourceProductsData = await queryClient.fetchQuery(
          ["products", lineItemsId, lineItemsType],
          async () => (await companyClient.get(productsPath)).data,
          { staleTime: 30000 }
        );
        const sourceProducts = Array.isArray(
          (sourceProductsData as { products?: unknown[] })?.products
        )
          ? ((sourceProductsData as { products: unknown[] }).products as Array<Record<string, unknown>>)
          : [];

        const appointmentQueryKey = ["products", String(appointmentId), "appointment"];
        const existingAppointmentProductsData =
          queryClient.getQueryData(appointmentQueryKey) ||
          (await queryClient.fetchQuery(
            appointmentQueryKey,
            async () =>
              (await companyClient.get(`/products/appointment/${appointmentId}`)).data
          ));
        const existingProducts = Array.isArray(
          (existingAppointmentProductsData as { products?: unknown[] })?.products
        )
          ? ((existingAppointmentProductsData as { products: unknown[] })
              .products as Array<Record<string, unknown>>)
          : [];
        const maxOrdering = existingProducts.length
          ? Math.max(
              ...existingProducts.map((product) => Number(product?.ordering) || 0)
            )
          : 0;
        const importedGroupIds = new Map<unknown, string>();
        let importedProductId = -1;
        const importedProducts = sourceProducts.map((product, idx) => {
          const groupId = product?.fk_ingroup_id;
          if (groupId != null && !importedGroupIds.has(groupId)) {
            importedGroupIds.set(
              groupId,
              `group_${groupId}_import_${Date.now()}_${importedGroupIds.size + 1}`
            );
          }
          const nextProduct: Record<string, unknown> = {
            ...product,
            id: importedProductId,
            new: true,
            ordering: maxOrdering + idx + 1,
          };
          importedProductId -= 1;
          if (groupId != null) {
            nextProduct.group_instance_id = importedGroupIds.get(groupId);
          }
          return nextProduct;
        });

        queryClient.setQueryData(appointmentQueryKey, {
          ...((existingAppointmentProductsData as object) || {}),
          products: [...existingProducts, ...importedProducts],
        });
        setPendingApptTypeLineItems(null);
        setApptTypeLineItemsAdded(true);
        setApptTypeLineItemSource(null);
        setApptTypeItemTargetId(null); // avoid re-importing at submit; items are now in local edit state
        showSnackbar(tr("Line items added in edit mode"));
        return;
      }
      const lineItemsQueryKey = ["products", lineItemsId, lineItemsType];
      await queryClient.prefetchQuery(
        lineItemsQueryKey,
        async () => (await companyClient.get(productsPath)).data,
        { staleTime: 30000 }
      );
      setApptTypeLineItemSource({
        itemTargetId,
        lineItemsType,
        lineItemsId,
        parentId,
      });
      setApptTypeItemTargetId(itemTargetId);
      setApptTypeLineItemsAdded(true);
      setPendingApptTypeLineItems(null);
    } catch (err) {
      console.error(err);
      showSnackbar(tr("Line items import failed"));
    } finally {
      setIsLineItemsPrefetching(false);
    }
  };

  const handleAppointmentLineItemsCancel = () => {
    if (!appointmentId) return;
    const appointmentQueryKey = ["products", String(appointmentId), "appointment"];
    queryClient.removeQueries(appointmentQueryKey, { exact: true });
    queryClient.invalidateQueries(appointmentQueryKey);
    setApptTypeLineItemsAdded(false);
  };

  // -----------------------------------------------------------------
  // Form state via native `useReducer` (audit 006 §14.Q1)
  // -----------------------------------------------------------------

  // Stable reference: this memo is load-bearing — `useFormState` resets
  // values when the reference changes (mirrors formik's
  // `enableReinitialize: true`). Recomputing the object every render would
  // wipe the user's typed values.
  const initialValues = useMemo<FormValues>(() => {
    const existingAppointmentDate = parseIsoDateWithinRange(
      appointment?.date ?? null
    );
    return {
      date: existingAppointmentDate
        ? format(existingAppointmentDate, "yyyy-MM-dd")
        : "",
      start_time_utc: appointment?.start_time_utc
        ? convertFromUTC(appointment.start_time_utc, timezone, false)
        : "",
      end_time_utc: appointment?.end_time_utc
        ? convertFromUTC(appointment.end_time_utc, timezone, false)
        : "",
      duration: minutesToTypedDigits(toDurationNumber(appointment?.duration)),
      assignee: appointment?.assignee || [],
      note: appointment?.note || "",
      estimate_amount:
        appointment?.estimate_amount != null ? appointment.estimate_amount / 100 : "",
      completion_note: appointment?.completion_note || "",
      type:
        appointment?.type === "request"
          ? "job"
          : (appointment?.type as FormValues["type"]) || "job",
      fk_appointment_type_id:
        appointment?.fk_appointment_type_id?.toString() || "",
      fk_appointment_type_default_id:
        appointment?.fk_appointment_type_default_id?.toString() || null,
      estimate_products: null,
      estimate_pricetype: "sale",
      is_reminding: isBookit
        ? appointment?.is_booked
          ? true
          : false
        : appointment?.is_reminding || false,
    };
  }, [appointment, timezone, isBookit]);

  const form = useFormState<FormValues>(initialValues);
  // Mirror state in a ref so the form-submit handler can read the latest
  // values synchronously (no stale closure).
  const formStateRef = React.useRef(form);
  formStateRef.current = form;

  // -----------------------------------------------------------------
  // Checklist rows — Phase 2b: lifted from the section into a hook
  // -----------------------------------------------------------------

  const fkAppointmentTypeId = form.values.fk_appointment_type_id ?? "";
  const fkAppointmentTypeDefaultId = form.values.fk_appointment_type_default_id ?? null;
  const checklistTypeNum = Number(fkAppointmentTypeId);
  const checklistDefaultNum = (() => {
    if (fkAppointmentTypeDefaultId == null || fkAppointmentTypeDefaultId === "") return null;
    const n = Number(fkAppointmentTypeDefaultId);
    return Number.isFinite(n) ? n : null;
  })();
  // Number("") === 0; treat 0 as "no type" (audit 005 §F2).
  const actualTypeOrNull =
    Number.isFinite(checklistTypeNum) && checklistTypeNum > 0 ? checklistTypeNum : null;
  const hasSubAppointmentDefaultPicker = defaultOptions.length > 1;
  const allowChecklistAutoOffer =
    selectedDefaultId !== "new" || !hasSubAppointmentDefaultPicker;

  /**
   * Saved (type, default) scope from the BE — derived from the appointment
   * prop. This is the authoritative source for "what scope is the appointment
   * actually in", regardless of what the user has picked on the FE since.
   */
  const savedScope = useMemo(() => {
    if (!appointment) return null;
    const t = appointment.fk_appointment_type_id;
    const d = appointment.fk_appointment_type_default_id;
    const typeN = t != null && t !== "" ? Number(t) : null;
    const defaultN = d != null && d !== "" ? Number(d) : null;
    return {
      type: Number.isFinite(typeN) && (typeN ?? 0) > 0 ? (typeN as number) : null,
      default: Number.isFinite(defaultN) ? (defaultN as number) : null,
    };
  }, [appointment]);

  const checklists = useChecklistRows({
    appointmentIdNum: appointmentId ? Number(appointmentId) : null,
    actualTypeOrNull,
    defaultNum: checklistDefaultNum,
    allowChecklistAutoOffer,
    savedScope,
  });
  // Wire the late-bound ref so `saveAppt.onSuccess` can call flushPending.
  flushChecklistsRef.current = checklists.flushPending;

  const handleFormSubmit = (e?: React.FormEvent) => {
    e?.preventDefault?.();
    const v = formStateRef.current.values;
    const errs = validate(v, invalidDateMessage);
    if (Object.keys(errs).length > 0) {
      formStateRef.current.setErrors(errs);
      return;
    }
    if (Object.keys(formStateRef.current.errors).length > 0) {
      formStateRef.current.setErrors({});
    }
    void onSubmit(v);
  };

  const applySource = async (
    source: AppointmentTypeRow | AppointmentTypeDefault,
    currentFormikValues: FormValues,
    currentSendEmail: boolean | undefined,
    extraFormPatch: Partial<FormValues> = {}
  ) => {
    const { changes, changedFields } = await buildDefaultChanges(source);

    setValuesBeforeDefaultFill({
      ...currentFormikValues,
      _sendEmail: currentSendEmail,
    });

    const modified = computeModifiedFields(changedFields, currentFormikValues, changes);
    setDefaultModifiedFields(modified);

    form.setValues({
      ...currentFormikValues,
      ...extraFormPatch,
      ...changes,
    });

    if (source.email_confirmation !== null && source.email_confirmation !== undefined) {
      setSendEmail(!!source.email_confirmation);
    }

    syncLineItemsOfferFromSource(source);
  };

  const handleTypeChange = async (e: { value?: string } | null) => {
    const newTypeId = e?.value || "";
    const previousTypeId = selectedTypeId;
    // The checklists section rebuilds deterministically from (scope,
    // allowAutoOffer) — no `requestAutoImport` opt-in needed (Phase 2a).

    if (!newTypeId) {
      lastAppliedChildDefaultRef.current = null;
      setSelectedTypeId("");
      if (valuesBeforeDefaultFill) {
        const { _sendEmail, ...prev } = valuesBeforeDefaultFill;
        form.setValues((cur) => ({
          ...cur,
          ...prev,
          fk_appointment_type_id: "",
          fk_appointment_type_default_id: null,
        }));
        if (_sendEmail !== undefined) setSendEmail(_sendEmail);
      } else {
        form.setValues((prev) => ({
          ...prev,
          fk_appointment_type_id: "",
          fk_appointment_type_default_id: null,
        }));
      }
      setSelectedDefaultId("new");
      setDefaultModifiedFields(new Set());
      setPendingApptTypeLineItems(null);
      setApptTypeLineItemsAdded(false);
      setApptTypeItemTargetId(null);
      setApptTypeLineItemSource(null);
      setValuesBeforeDefaultFill(null);
      return;
    }

    if (String(newTypeId) === String(previousTypeId)) {
      setSelectedTypeId(newTypeId);
      form.setValues((prev) => ({ ...prev, fk_appointment_type_id: String(newTypeId) }));
      return;
    }

    lastAppliedChildDefaultRef.current = null;
    setSelectedTypeId(newTypeId);

    const newType = (appointmentTypesQuery.data || []).find(
      (tp) => String(tp.id) === String(newTypeId)
    );

    if (!newType) {
      form.setValues((prev) => ({
        ...prev,
        fk_appointment_type_id: String(newTypeId),
        fk_appointment_type_default_id: null,
      }));
      setSelectedDefaultId("new");
      return;
    }

    const hasParentDefaults = Boolean(
      newType.description ||
        newType.duration ||
        newType.assigned_to ||
        newType.completion_note ||
        newType.date ||
        newType.start_time_utc ||
        newType.end_time_utc ||
        newType.category ||
        newType.send_reminder !== undefined ||
        newType.email_confirmation != null
    );

    if (hasParentDefaults || newType.item_target_id) {
      const currentVals: FormValues = {
        ...form.values,
        fk_appointment_type_id: String(newTypeId),
      };
      await applySource(newType, currentVals, sendEmail, {
        fk_appointment_type_id: String(newTypeId),
        fk_appointment_type_default_id: null,
      });

      const activeDefaults = (newType.defaults || []).filter((d) => d.active);
      if (activeDefaults.length > 0) {
        isProgrammaticDefaultChange.current = true;
        setSelectedDefaultId("parent-applied");
      } else {
        setSelectedDefaultId("new");
      }
    } else {
      form.setValues((prev) => ({
        ...prev,
        fk_appointment_type_id: String(newTypeId),
        fk_appointment_type_default_id: null,
      }));
      setSelectedDefaultId("new");
      setDefaultModifiedFields(new Set());
      syncLineItemsOfferFromSource({});
    }
  };

  const handleDefaultChange = async (e: { value?: string } | null) => {
    const raw = e?.value;
    const defaultValue =
      raw === undefined || raw === null || raw === "" ? "new" : String(raw);
    if (isProgrammaticDefaultChange.current && defaultValue === "parent-applied") {
      isProgrammaticDefaultChange.current = false;
      return;
    }
    // The checklists section rebuilds deterministically from (scope,
    // allowAutoOffer) — no `requestAutoImport` opt-in needed (Phase 2a).
    setSelectedDefaultId(defaultValue);

    if (defaultValue === "new") {
      lastAppliedChildDefaultRef.current = null;
      if (valuesBeforeDefaultFill) {
        const { _sendEmail, ...prev } = valuesBeforeDefaultFill;
        form.setValues((cur) => ({
          ...cur,
          ...prev,
          fk_appointment_type_default_id: null,
        }));
        if (_sendEmail !== undefined) setSendEmail(_sendEmail);
      } else {
        form.setValues((cur) => ({ ...cur, fk_appointment_type_default_id: null }));
      }
      setDefaultModifiedFields(new Set());
      setPendingApptTypeLineItems(null);
      setApptTypeLineItemsAdded(false);
      setApptTypeItemTargetId(null);
      setApptTypeLineItemSource(null);
      return;
    }

    if (defaultValue === "parent-applied") {
      lastAppliedChildDefaultRef.current = null;
      if (selectedType) {
        const baseVals = valuesBeforeDefaultFill
          ? { ...valuesBeforeDefaultFill }
          : { ...form.values };
        const { _sendEmail, ...rest } = baseVals as ValuesSnapshotForRevert;
        const currentVals = rest as FormValues;
        const savedEmail = valuesBeforeDefaultFill?._sendEmail ?? sendEmail;
        await applySource(selectedType, currentVals, savedEmail, {
          fk_appointment_type_default_id: null,
        });
      }
      return;
    }

    if (!selectedType) {
      return;
    }
    const childDefault = (selectedType.defaults || []).find(
      (d) => String(d.id) === String(defaultValue)
    );
    if (!childDefault) {
      return;
    }

    if (lastAppliedChildDefaultRef.current === String(defaultValue)) {
      return;
    }
    lastAppliedChildDefaultRef.current = String(defaultValue);

    const currentVals: FormValues = {
      ...form.values,
      fk_appointment_type_default_id: String(defaultValue),
    };
    const savedEmail = valuesBeforeDefaultFill?._sendEmail ?? sendEmail;
    await applySource(childDefault, currentVals, savedEmail, {
      fk_appointment_type_default_id: String(defaultValue),
    });

    // If child has no line items but parent does, offer parent's template
    if (!childDefault.item_target_id && selectedType.item_target_id) {
      syncLineItemsOfferFromSource(selectedType);
    }
  };

  const revertField = (fieldName: string) => {
    if (!valuesBeforeDefaultFill) return;
    if (fieldName === "sendEmail") {
      setSendEmail(valuesBeforeDefaultFill._sendEmail);
    } else {
      form.setFieldValue(fieldName, (valuesBeforeDefaultFill as Record<string, unknown>)[fieldName]);
    }
    setDefaultModifiedFields((prev) => {
      const next = new Set(prev);
      next.delete(fieldName);
      return next;
    });
  };

  const FieldLabel = ({ text, fieldName }: { text: string; fieldName: string }) => {
    const isModified = defaultModifiedFields.has(fieldName);
    const [hovered, setHovered] = useState(false);
    return (
      <label>
        {isModified && (
          <TooltippedElement
            message={tr("Undo")}
            className="mt-[5px]"
            element={
              <button
                type="button"
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onClick={() => revertField(fieldName)}
              >
                {hovered ? (
                  <CgUndo size={20} className="text-blue-500" />
                ) : (
                  <IoMdInformationCircleOutline size={20} className="text-orange-400" />
                )}
              </button>
            }
          />
        )}
        {text}
      </label>
    );
  };

  const handleAddToDescription = () => {
    const responsesString = responses
      .map((response, index) => {
        return `${index + 1}: ${response.question_label}\n${response.options
          .map((option) => option.response)
          .join("\n")}`;
      })
      .join("\n");
    form.setFieldValue("note", (form.values.note ?? "") + "\n\n" + responsesString);
  };

  useEffect(() => {
    setSendEmail(appointment?.is_booked ? false : true);
    getAppointmentEmailConfigMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------------------------------------------
  // Submit
  // -----------------------------------------------------------------

  const onSubmit = async (e: FormValues) => {
    const parsedSubmitDate = parseIsoDateWithinRange(e.date ?? null);
    if (e.date && !parsedSubmitDate) {
      showSnackbar(invalidDateMessage);
      return;
    }

    const tempDate = parsedSubmitDate
      ? format(parsedSubmitDate, "yyyy-MM-dd")
      : format(new Date(), "yyyy-MM-dd");

    const formatData = mapValues(e, (value, key) => {
      if (key === "duration") {
        return toApiDurationString(value);
      }
      if (!value && value !== false) return null;
      if (key === "start_time_utc") {
        return convertToUTC(tempDate, value as string, timezone);
      }
      if (key === "end_time_utc") {
        return convertToUTC(tempDate, value as string, timezone);
      }
      return value;
    });

    const shouldApplyApptTypeLineItems =
      apptTypeLineItemsAdded && apptTypeItemTargetId != null;
    const apptTypeLineItemsPayload = shouldApplyApptTypeLineItems
      ? { appt_type_item_target_id: apptTypeItemTargetId }
      : {};

    if (appointment) {
      saveAppt.mutate({
        mode: "update",
        payload: {
          ...formatData,
          ...apptTypeLineItemsPayload,
          notify: sendEmail && appointmentEmailConfig,
          isBookit,
          assignee: e.assignee,
        },
      });
      return;
    }

    if (mdatelist.length >= 1) {
      const parentRes = await saveAppt.mutateAsync({
        mode: "create",
        payload: {
          ...formatData,
          ...apptTypeLineItemsPayload,
          parentId,
          parentType,
          parts: [],
          notify: sendEmail && appointmentEmailConfig,
          parent_app: null,
        },
      });
      const parentApptId = parentRes?.id;
      mdatelist.forEach((a) => {
        const dt = a.toDate();
        const multiDayDate = format(dt, "yyyy-MM-dd");
        const multiDayFormatData = {
          ...formatData,
          start_time_utc: convertToUTC(multiDayDate, e.start_time_utc, timezone),
          end_time_utc: convertToUTC(multiDayDate, e.end_time_utc, timezone),
        };
        delete (multiDayFormatData as Record<string, unknown>).date;
        saveAppt.mutate({
          mode: "create",
          payload: {
            ...multiDayFormatData,
            ...apptTypeLineItemsPayload,
            parentId,
            parentType,
            parts: [],
            notify: false,
            parent_app: parentApptId ?? null,
            date: dt.toDateString(),
          },
          isMultiDaySibling: true,
        });
      });
      setModal();
      return;
    }

    await saveAppt.mutateAsync({
      mode: "create",
      payload: {
        ...formatData,
        ...apptTypeLineItemsPayload,
        parentId,
        parentType,
        parts: [],
        notify: sendEmail && appointmentEmailConfig,
        parent_app: null,
      },
    });
    setModal();
  };

  const [Enddate, setEnddate] = React.useState<string>(() => "none");
  const [mdatelist, setmdatelist] = React.useState<DateObject[]>(() => []);

  const switchMulti = () => {
    setEnddate((s) => (s === "none" ? "inline-block" : "none"));
  };

  return (
    <AppointmentModalForm onSubmit={handleFormSubmit}>
      <SHeader>
        <div className="w-[100%] flex flex-row items-center justify-between">
          <h2>{tr("Info")}</h2>
          <div className="text-blue-500 underline hover:text-blue-700">
            <NoStyleButton
              size="small"
              className=""
              onClick={handleShowRequestData}
            >
              <span className="text-blue-500 underline hover:text-blue-700">
                {isFlyoutVisible ? "Hide" : "Show"} {tr("Responses")}
              </span>
            </NoStyleButton>
            {isFlyoutVisible && (
              <div className="absolute right-0 z-10 p-2 mt-2 bg-white border rounded shadow-lg min-w-[400px] text-black">
                <div className="p-4 text-lg font-extrabold uppercase">
                  {tr("Responses")}
                </div>
                <hr />
                <div className="overflow-y-scroll min-h-[200px] cursor-text">
                  {responses && responses.length > 0 ? (
                    responses.map((response, index) => (
                      <div key={response.id} className="mb-4">
                        <div className="font-bold">
                          {index + 1}: {response.question_label}
                        </div>
                        {response &&
                        response.options &&
                        response.options.length > 0 ? (
                          response.options.map((option, idx) => (
                            <div key={idx} className="ml-2">
                              {option.response}
                            </div>
                          ))
                        ) : (
                          <div>{tr("No response")}</div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div>{tr("No responses")}</div>
                  )}
                </div>
                <Button onClick={handleAddToDescription} primary>
                  {tr("Add to Description")}
                </Button>
              </div>
            )}
          </div>
        </div>
      </SHeader>
      <SContent>
        {!has_est && !appointment && form.values.type === "job" && (
          <div className="flex p-2 my-2 text-yellow-600 border border-yellow-600 rounded bg-yellow-100/50">
            <VscWarning style={{ fontSize: "1.5rem", marginRight: "1rem" }} />
            <p>
              {tr(
                "No estimate has marked as won yet. To get an accurate production value, please mark an estimate as won before proceeding to create appointments."
              )}
            </p>
          </div>
        )}

        <Row>
          <FieldLabel text={tr("Category")} fieldName="type" />
          <GroupedButton>
            <OutlineButton
              onClick={() => form.setFieldValue("type", "estimate")}
              $selected={form.values.type === "estimate"}
            >
              {tr("Estimate")}
            </OutlineButton>
            <OutlineButton
              onClick={() => form.setFieldValue("type", "job")}
              $selected={form.values.type === "job"}
            >
              {tr("Job")}
            </OutlineButton>
          </GroupedButton>
        </Row>
        <Row>
          <label>{tr("Type")}</label>
          <div className="flex gap-2">
            <AppointmentType
              className="!min-w-[200px]"
              value={form.values.fk_appointment_type_id}
              onChange={handleTypeChange}
              isClearable
            />
            {selectedTypeId && defaultOptions.length > 1 && (
              <Field
                type="select"
                className="!min-w-[200px]"
                options={defaultOptions}
                value={selectedDefaultOption}
                onChange={handleDefaultChange}
                placeholder={tr("Select default...")}
              />
            )}
            {selectedTypeId &&
            (checklistDefaultsCountQuery.isLoading ||
              checklists.displayRows.length > 0 ||
              (checklistDefaultsCountQuery.data ?? 0) > 0) ? (
              <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full whitespace-nowrap self-center">
                {checklistDefaultsCountQuery.isLoading && checklists.displayRows.length < 1
                  ? tr("Loading ellipsis")
                  : t(`${tBase}.Checklist defaults count`, {
                      count:
                        checklists.displayRows.length > 0
                          ? checklists.displayRows.length
                          : checklistDefaultsCountQuery.data ?? 0,
                    })}
              </span>
            ) : null}
          </div>
        </Row>
        <Row>
          <FieldLabel text={tr("Date")} fieldName="date" />
          <Input
            type="date"
            name="date"
            placeholder={"Date"}
            value={form.values.date}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              form.setFieldValue("date", e.target.value)
            }
            onBlur={() => form.markTouched("date")}
            error={form.touched.date ? form.errors.date : undefined}
          />
        </Row>
        <Row>
          <label>{tr("Multi day appointment")}</label>
          <Switch onChange={switchMulti} />
          <label style={{ display: Enddate }}>{tr("Enter Other Dates")}</label>
          <DatePicker
            multiple
            style={{
              display: Enddate,
              width: "100%",
              height: "40px",
              background: "rgba(0,0,0,0)",
              border: "1px solid #d9d9d9",
              padding: "0 1em",
            }}
            value={mdatelist}
            onChange={setmdatelist}
          />
        </Row>
        <Row>
          <FieldLabel text={tr("Arrival Time Window")} fieldName="start_time_utc" />
          <TimeCon>
            <Input
              type="time"
              name="start_time_utc"
              placeholder={tr("Start Time")}
              value={form.values.start_time_utc}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                form.setFieldValue("start_time_utc", e.target.value)
              }
              onBlur={() => form.markTouched("start_time_utc")}
              error={
                form.touched.start_time_utc ? form.errors.start_time_utc : undefined
              }
            />
            <span style={{ margin: "0 0.5rem 0 0.5rem" }}>{"-"}</span>
            <Input
              type="time"
              name="end_time_utc"
              placeholder={tr("End Time")}
              value={form.values.end_time_utc}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                form.setFieldValue("end_time_utc", e.target.value)
              }
              onBlur={() => form.markTouched("end_time_utc")}
              error={
                form.touched.end_time_utc ? form.errors.end_time_utc : undefined
              }
            />
          </TimeCon>
        </Row>
        <Row>
          <FieldLabel text={tr("Duration")} fieldName="duration" />
          <MaskedDurationInput
            name="duration"
            value={form.values.duration}
            onValueChange={(digits) => {
              if (form.values.duration !== digits) {
                form.setFieldValue("duration", digits);
              }
            }}
          />
        </Row>
        <Row>
          <FieldLabel text={tr("Assign to")} fieldName="assignee" />
          <AssigneeSelect
            assigneeType="appointment"
            name="assignee"
            value={form.values.assignee}
            onChange={(e: AssigneeOption[]) => {
              form.setValues((v) => ({ ...v, assignee: e }));
            }}
          />
        </Row>
        <Row>
          <FieldLabel text={tr("Description")} fieldName="note" />
          <Field
            name="note"
            type="textarea"
            minRows={4}
            value={form.values.note}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              form.setFieldValue("note", e.target.value)
            }
          />
        </Row>
        <Row>
          <FieldLabel text={tr("Completion Note")} fieldName="completion_note" />
          <Field
            name="completion_note"
            type="textarea"
            minRows={4}
            value={form.values.completion_note}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              form.setFieldValue("completion_note", e.target.value)
            }
          />
        </Row>
      </SContent>

      <SContent>
        <AppointmentModalChecklistsSection
          checklists={checklists}
          fkAppointmentTypeId={fkAppointmentTypeId}
          actualTypeOrNull={actualTypeOrNull}
          defaultNum={checklistDefaultNum}
          selectedDefaultMode={selectedDefaultId ?? null}
          hasSubAppointmentDefaultPicker={hasSubAppointmentDefaultPicker}
          isCompleted={appointment?.status?.label === "Completed"}
        />
      </SContent>

      {/* Default line items from appointment type: optional until user clicks Add + */}
      <SHeader
        style={{
          justifyContent: "flex-start",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        <h2 style={{ margin: 0 }}>{tr("Line Items for Appointment")}</h2>
        {pendingApptTypeLineItems &&
          !apptTypeLineItemsAdded &&
          (pendingLineItemsCountQuery.isLoading || pendingLineItemsOfferCount > 0) && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                primary
                onClick={() => {
                  void handleAddAppointmentTypeLineItems();
                }}
                disabled={
                  pendingLineItemsCountQuery.isLoading ||
                  pendingLineItemsOfferCount < 1 ||
                  isLineItemsPrefetching
                }
              >
                {tr("Add plus")}
              </Button>
              <span className="text-sm text-gray-600">
                {pendingLineItemsCountQuery.isLoading
                  ? tr("Loading ellipsis")
                  : t(`${tBase}.Appt type line items offer`, {
                      count: pendingLineItemsOfferCount,
                      label: selectedType?.label || "",
                    })}
              </span>
            </div>
          )}
      </SHeader>
      <SContent>
        {appointment && appointmentId ? (
          <LineItemsContainer
            key={`appointment-lineitems-${appointmentId}`}
            view={apptTypeLineItemsAdded ? "edit" : "read"}
            onCancel={handleAppointmentLineItemsCancel}
            type="appointment"
            id={String(appointmentId)}
            parentId={String(id)}
            parentType={type as "request" | "project"}
            noTax={true}
          />
        ) : (
          <LineItemsContainer
            key={
              apptTypeLineItemSource && apptTypeLineItemsAdded
                ? `${apptTypeLineItemSource.lineItemsType}-${apptTypeLineItemSource.lineItemsId}`
                : "appointment-type-lineitems-empty"
            }
            view={apptTypeLineItemsAdded ? "edit" : "read"}
            type={apptTypeLineItemSource?.lineItemsType ?? "appointment-type"}
            id={
              apptTypeLineItemSource && apptTypeLineItemsAdded
                ? apptTypeLineItemSource.lineItemsId
                : "new"
            }
            parentId={
              apptTypeLineItemSource && apptTypeLineItemsAdded
                ? apptTypeLineItemSource.parentId
                : undefined
            }
            noTax={true}
          />
        )}
      </SContent>

      {!appointment && (
        <>
          <SHeader>
            <h2>{tr("Estimate")}</h2>
            <Switch
              style={{ marginLeft: "auto" }}
              checked={isEstimating}
              onChange={setIsEstimating}
            />
          </SHeader>
          <SContent>
            {isEstimating && (
              <LineItemsContainer
                view="edit"
                type="estimate_appt"
                parentType={type as "request" | "project"}
                id={String(id)}
                onClick={(p) => {
                  form.setFieldValue("estimate_products", p.products);
                  form.setFieldValue("estimate_pricetype", p.price_type);
                }}
                noTax={true}
              />
            )}
          </SContent>
        </>
      )}
      <div
        className="space-x-4"
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        {type !== "projects" && (
          <>
            <div className="space-x-2">
              <span>{tr("Send Reminder")}</span>
              <Switch
                checked={form.values.is_reminding}
                onChange={(e) => form.setFieldValue("is_reminding", e)}
              />
            </div>
            <div className="space-x-2">
              <span style={{ color: "grey", fontSize: "85%" }}>
                {tr("Send E-mail Confirmation to Customer")}
              </span>
              <Switch
                checked={Boolean(sendEmail && appointmentEmailConfig)}
                onChange={(e) => {
                  setSendEmail(e);
                  setAppointmentEmailConfig(e);
                }}
              />
            </div>
          </>
        )}
        <Button type="submit" primary isLoading={saveAppt.isLoading}>
          {isBookit
            ? appointment?.is_booked
              ? tr("Submit")
              : "Book It"
            : tr("Submit")}
        </Button>
      </div>
    </AppointmentModalForm>
  );
}

// ============================================================================
// Styles
// ============================================================================

const AppointmentModalForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 100vw;
  max-width: 1000px;
  overflow-x: none;
`;

const Row = styled.div`
  margin: 0.5rem 0;
  display: grid;
  align-items: center;
  grid-template-columns: 1fr 3fr;
  gap: 1rem;
  > label {
    text-align: right;
    font-weight: bold;
    font-size: 0.8rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 0.25rem;
  }
`;

const TimeCon = styled.div`
  display: flex;
  align-items: center;
  margin: 0.5rem 0;
  > div {
    flex: 1;
  }
`;

const SHeader = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${theme.border.dark};
  padding: 1rem 0;
`;

const SContent = styled.div`
  display: flex;
  flex-direction: column;
`;
